import _get from 'lodash/get';
import _set from 'lodash/set';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Form } from 'grab-ui';
import FeedbackModal from 'components/history/modal/feedback-modal';
import MSDTrackingModal from 'components/history/modal/msd-tracking-modal';
import CancelMultipleConfirmModal from 'components/history/modal/cancel-multiple-confirm-modal';
import { getCompanyID } from 'helper/user';
import { cat, custom, event, trackClick } from 'common/utils/instrumentation';
import {
  GE_WEB_ORDER_STATE,
  MSD_STEP_STATE,
  ORDER_TYPES,
} from 'constants/order-constants';
import { PAX_HISTORY_FETCH_LIMIT } from 'constants/history-constants';
import OngoingBookingsSection from 'components/v2/ongoing-bookings-section/ongoing-bookings-section';
import PreviousBookingsSection from 'components/v2/previous-bookings-section/previous-bookings-section';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BOOKING_EVENTS,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import { isShopifyOrder } from 'helper/order';
import css from './grab-id-order-history.less';

const getActionDropdownID = order => `ant-dropdown-${order.deliveryID}`;

class GrabIDOrderHistory extends Component {
  static contextType = ShopifyTrackEventContext;
  static getDerivedStateFromProps(props, state) {
    const { selectedOngoingState } = state;
    const { ongoingOrders, isOngoingOrdersLoading } = props;

    return {
      hideOngoingBookingsSection:
        selectedOngoingState === GE_WEB_ORDER_STATE.ActiveDelivery &&
        !isOngoingOrdersLoading &&
        _isEmpty(ongoingOrders),
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      cancelOrderModal: {
        open: false,
        deliveryID: '',
        expressServiceID: undefined,
        isMSDBooking: false,
        shopifyOrderIDs: [],
        isShopifyOrder: false,
      },
      cancelMultipleOrderModel: {
        open: false,
      },
      trackingLinksModal: {
        open: false,
        links: [],
      },
      selectedOngoingState:
        _get(props, 'location.state.ongoingState') ||
        GE_WEB_ORDER_STATE.ActiveDelivery,
      hideOngoingBookingsSection: true,
      selectedOngoingOrderType: ORDER_TYPES.ALL,
      selectedOrders: [],
    };
  }

  // eslint-disable-next-line react/sort-comp
  hideActionDropdown = order => {
    try {
      const actionDropdown = document.getElementById(
        getActionDropdownID(order),
      );
      actionDropdown.click();
    } catch (e) {
      // do nothing
    }
  };

  openMSDTrackingModal = (links = []) => {
    this.setState({
      trackingLinksModal: { open: true, links },
    });
  };

  closeMSDTrackingModal = () => {
    this.setState({
      trackingLinksModal: { open: false },
    });
  };

  onClickTrackOrder = order => () => {
    const { steps, trackingURL, webTrackingURLsMap } = order;
    const ongoingRecipient = steps
      .slice(1)
      .find(
        step => step.status === MSD_STEP_STATE.Ongoing && step.webTrackingURL,
      );

    if (!_isEmpty(ongoingRecipient)) {
      window.open(ongoingRecipient.webTrackingURL);
      this.hideActionDropdown(order);
      return;
    }

    if (!_isEmpty(webTrackingURLsMap)) {
      this.openMSDTrackingModal(webTrackingURLsMap);
      return;
    }

    window.open(trackingURL);
    this.hideActionDropdown(order);
  };

  onClickCancelOrder = order => () => {
    const isMSDBooking = _get(order, 'steps.length') > 2;
    const expressServiceID = _get(order, 'service.expressServiceID', 0);
    const shopifyMetaDatas = _get(order, 'shopifyMetaData', []);
    const shopifyOrderIDs = shopifyMetaDatas.map(metadata => metadata.ID);
    this.props.listCancellationReasons([order]);
    this.setState({
      cancelOrderModal: {
        open: true,
        deliveryID: order.deliveryID,
        expressServiceID,
        isMSDBooking,
        isShopifyOrder: isShopifyOrder(_get(order, 'thirdPartySource', '')),
        shopifyOrderIDs,
      },
    });
    this.hideActionDropdown(order);
  };

  onSelectOrders = (selectedOrdersKeys, selectedOrders) => {
    this.setState({ selectedOrders });
  };

  onClickCancelMultipleOrders = orders => {
    this.setState({
      cancelMultipleOrderModel: {
        open: true,
      },
    });
    this.setState({
      cancelOrderModal: {
        open: false,
        isMultiple: true,
        orders,
      },
    });
  };

  onSubmitCancelMultipleOrder = () => {
    const { selectedOrders } = this.state;
    this.props.listCancellationReasons(selectedOrders);
    this.setState({
      cancelMultipleOrderModel: {
        open: false,
      },
    });
    this.setState({
      cancelOrderModal: {
        open: true,
        isMultiple: true,
        orders: selectedOrders,
      },
    });
  };

  clearSelectedOrders = () => {
    this.setState({
      selectedOrders: [],
    });
  };

  onHideCancelModal = cancelReq => {
    const { cancelOrderModal } = this.state;
    const {
      cancelOrder,
      submitCancelReason,
      enableWebCancellation,
    } = this.props;

    if (_get(cancelOrderModal, 'isMultiple', false)) {
      this.cancelMultipleOrders(cancelReq);
    } else if (enableWebCancellation) {
      cancelOrder({
        deliveryID: cancelOrderModal.deliveryID,
        expressServiceID: cancelOrderModal.expressServiceID,
      });
      submitCancelReason(cancelReq);
    }
    this.setState({ cancelOrderModal: {} });
    this.clearSelectedOrders();
  };

  onHideCancelMultipleModel = () => {
    this.setState({
      cancelMultipleOrderModel: {
        open: false,
      },
    });
  };

  cancelMultipleOrders = cancelReq => {
    const { selectedOrders } = this.state;
    const {
      submitCancelReasonsForMultipleOrders,
      enableWebCancellation,
    } = this.props;
    const multipleCancelReq = selectedOrders.map(order => {
      const newReq = {
        ..._cloneDeep(cancelReq),
      };
      const isMSDBooking = _get(order, 'steps.length') > 2;
      const expressServiceID = _get(order, 'service.expressServiceID', 0);
      _set(newReq, 'req.deliveryID', order.deliveryID);
      _set(newReq, 'req.isMSDBooking', isMSDBooking);
      _set(newReq, 'req.expressServiceID', expressServiceID);
      return newReq;
    });

    if (enableWebCancellation) {
      submitCancelReasonsForMultipleOrders(multipleCancelReq);
    }
  };

  onSubmitCancelOrder = cancelReq => {
    const { cancelOrderModal } = this.state;
    const {
      cancelOrder,
      submitCancelReason,
      enableWebCancellation,
    } = this.props;

    if (_get(cancelOrderModal, 'isMultiple', false)) {
      this.cancelMultipleOrders(cancelReq);
    } else {
      cancelOrder({
        deliveryID: cancelOrderModal.deliveryID,
        expressServiceID: cancelOrderModal.expressServiceID,
      });
      submitCancelReason(cancelReq);

      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.CANCEL_BOOKING,
        facets: {
          [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]:
            cancelOrderModal.shopifyOrderIDs,
          [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]:
            cancelOrderModal.isMSDBooking,
          [SCRIBE_SHOPIFY_PARAMS.IS_SHOPIFY_ORDER]:
            cancelOrderModal.isShopifyOrder,
        },
      });
    }

    this.setState({
      cancelOrderModal: {},
    });
    if (enableWebCancellation) {
      this.clearSelectedOrders();
    }
  };

  onSelectOngoingState = value => {
    const { selectedOngoingState } = this.state;
    const { userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(
      event.BOOKINGS_CLICKS,
      cat.GE_BOOKINGS,
      'ONGOING_BOOKINGS_STATUS',
      {
        [custom.COMPANY_ID]: companyID,
      },
    );

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.ONGOING_BOOKINGS_STATUS,
      facets: getCommonFacet(userInfo),
    });

    if (selectedOngoingState !== value) {
      this.setState({ selectedOngoingState: value });
    }
  };

  onSelectOngoingOrderType = value => {
    const { selectedOngoingOrderType } = this.state;
    const { userInfo } = this.props;

    if (value === ORDER_TYPES.SHOPIFY) {
      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_SHOPIFY_ORDERS,
      });
    }

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.ONGOING_BOOKINGS_FILTER,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.ONGOING_FILTER_NAME]: I18n.t(
          `history.orderTypes.${value.toLowerCase()}`,
          { locale: 'en' },
        ),
      },
    });

    if (selectedOngoingOrderType !== value) {
      this.setState({ selectedOngoingOrderType: value });
    }
  };

  renderOngoingBookingsSection = () => {
    const {
      selectedOngoingState,
      hideOngoingBookingsSection,
      selectedOngoingOrderType,
    } = this.state;

    if (hideOngoingBookingsSection) {
      return null;
    }

    const {
      ongoingOrders,
      ongoingOrdersLastUpdate,
      isOngoingOrdersLoading,
      userInfo,
      enableWebCancellation,
      onClickPrintLabel,
      onClickBulkPrintLabel,
      scribeExpressBookingsWebShowOngoingBookings,
    } = this.props;

    const { selectedOrders } = this.state;

    return (
      <OngoingBookingsSection
        ongoingOrders={ongoingOrders}
        selectedOrders={selectedOrders}
        ongoingOrdersLastUpdate={ongoingOrdersLastUpdate}
        isOngoingOrdersLoading={isOngoingOrdersLoading}
        userInfo={userInfo}
        onClickTrackOrder={this.onClickTrackOrder}
        onClickCancelOrder={this.onClickCancelOrder}
        onClickCancelMultipleOrders={this.onClickCancelMultipleOrders}
        onSelectOngoingState={this.onSelectOngoingState}
        selectedOngoingState={selectedOngoingState}
        onSelectOngoingOrderType={this.onSelectOngoingOrderType}
        selectedOngoingOrderType={selectedOngoingOrderType}
        onSelectOrders={this.onSelectOrders}
        onClickPrintLabel={onClickPrintLabel}
        onClickBulkPrintLabel={onClickBulkPrintLabel}
        scribeExpressBookingsWebShowOngoingBookings={
          scribeExpressBookingsWebShowOngoingBookings
        }
        enableWebCancellation={enableWebCancellation}
      />
    );
  };

  renderPreviousBookingsSection = () => {
    const {
      userInfo,
      completedOrders,
      completedOrdersSourceOptions,
      updateOrderShallow,
      bookAgain,
      shopifyBookAgain,
      form,
      isPreviousBookingsLoading,
      fetchAllPreviousBookings,
    } = this.props;

    return (
      <PreviousBookingsSection
        userInfo={userInfo}
        completedOrders={completedOrders}
        completedOrdersSourceOptions={completedOrdersSourceOptions}
        updateOrderShallow={updateOrderShallow}
        bookAgain={bookAgain}
        shopifyBookAgain={shopifyBookAgain}
        isPreviousBookingsLoading={isPreviousBookingsLoading}
        form={form}
        fetchAllPreviousBookings={fetchAllPreviousBookings}
        isPaxUser
      />
    );
  };

  render() {
    const {
      trackingLinksModal,
      cancelOrderModal,
      selectedOrders,
      cancelMultipleOrderModel,
    } = this.state;
    const { cancelReasons } = this.props;

    return (
      <div>
        <h4 className={css.pageHeader}>{I18n.t('appSider.bookings')}</h4>
        {this.renderOngoingBookingsSection()}
        {this.renderPreviousBookingsSection()}
        <CancelMultipleConfirmModal
          orderAmt={selectedOrders.length}
          handleCancelOrderModal={this.onSubmitCancelMultipleOrder}
          handleClose={this.onHideCancelMultipleModel}
          visible={cancelMultipleOrderModel.open}
        />
        <FeedbackModal
          visible={cancelOrderModal.open}
          closeable
          deliveryID={cancelOrderModal.deliveryID}
          expressServiceID={cancelOrderModal.expressServiceID}
          isMSDBooking={cancelOrderModal.isMSDBooking}
          cancelReasons={cancelReasons}
          handleSubmit={this.onSubmitCancelOrder}
          onCancel={this.onHideCancelModal}
        />
        <MSDTrackingModal
          visible={trackingLinksModal.open}
          trackingURLsMap={trackingLinksModal.links}
          handleClose={this.closeMSDTrackingModal}
        />
      </div>
    );
  }
}

GrabIDOrderHistory.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  ongoingOrders: PropTypes.arrayOf(PropTypes.shape({})),
  isOngoingOrdersLoading: PropTypes.bool.isRequired,
  ongoingOrdersLastUpdate: PropTypes.string,
  cancelReasons: PropTypes.arrayOf(PropTypes.shape({})),
  cancelOrder: PropTypes.func.isRequired,
  updateOrderShallow: PropTypes.func.isRequired,
  bookAgain: PropTypes.func.isRequired,
  shopifyBookAgain: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  completedOrders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  completedOrdersSourceOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  isPreviousBookingsLoading: PropTypes.bool.isRequired,
  fetchAllPreviousBookings: PropTypes.func.isRequired,
  submitCancelReason: PropTypes.func.isRequired,
  submitCancelReasonsForMultipleOrders: PropTypes.func.isRequired,
  listCancellationReasons: PropTypes.func.isRequired,
  onClickPrintLabel: PropTypes.func.isRequired,
  onClickBulkPrintLabel: PropTypes.func.isRequired,
  scribeExpressBookingsWebShowOngoingBookings: PropTypes.func.isRequired,
  enableWebCancellation: PropTypes.bool.isRequired,
};

GrabIDOrderHistory.defaultProps = {
  ongoingOrders: [],
  ongoingOrdersLastUpdate: '',
  cancelReasons: [],
  completedOrders: [],
  completedOrdersSourceOptions: [],
  isPreviousBookingsLoading: false,
};

const onFieldsChange = _debounce((props, changedValues, allValues) => {
  const { fetchAllPreviousBookings } = props;

  const startTimestamp = allValues.DATE_RANGE.value[0].startOf('day').valueOf();
  const endTimestamp = allValues.DATE_RANGE.value[1].endOf('day').valueOf();
  const fetchPrevBookingParams = {
    startTimestamp,
    endTimestamp,
    limit: PAX_HISTORY_FETCH_LIMIT,
  };

  fetchAllPreviousBookings(fetchPrevBookingParams);
}, 700);

export default Form.create({ onFieldsChange })(GrabIDOrderHistory);
